import type { ReactNode } from "react";
import { kindOf, type PlanDimension } from "./plan-annotations.js";
import { dimensionReading } from "./plan-measure-marks.jsx";
import type { DimensionsHome } from "./use-plan-dimensions.js";

/**
 * The hand-placed dimensions as text: what each measures, what it is called,
 * and a real button to take it away.
 *
 * Not a convenience copy of the × on the drawing. It is the keyboard and
 * screen-reader path to the same operations — select, label, remove — and it
 * is where the storage is stated, next to the things it applies to, rather
 * than in a tooltip somebody has to go looking for: saved with the survey and
 * shared (since 2026-09-11), or — without an API to save to — in this browser
 * only.
 */

/** What the tool in hand is measuring, for the hint. */
export type MeasureKind = "line" | "angle" | "area";

const KIND_WORD: Readonly<Record<MeasureKind, string>> = { line: "length", angle: "angle", area: "area" };

function hintFor(kind: MeasureKind, placed: number): string {
  switch (kind) {
    case "line":
      return placed > 0
        ? "Now click the second point. Esc cancels."
        : "Click two points on the plan. Ends snap to corners, then to walls.";
    case "angle":
      return placed === 0
        ? "Click the end of the first leg, then the corner, then the end of the second leg."
        : placed === 1
          ? "Now click the corner. Esc cancels."
          : "Now click the end of the second leg. Esc cancels.";
    case "area":
      return placed < 3
        ? "Click round the area, corner by corner. Points snap to corners, then to walls."
        : "Keep clicking corners. Click the first point, double-click or press Enter to close it.";
  }
}

export function PlanDimensionsPanel({
  placement,
  measuring,
  measureKind = "line",
  pending,
  pendingPoints,
  dimensions,
  selectedId,
  home,
  moved = 0,
  stranded = 0,
  error = null,
  onDismissError,
  onSelect,
  onRemove,
  onLabel,
  readOnly = false,
}: {
  /** Beside the drawing when the frame is wide, under it when it is not. */
  readonly placement: "side" | "below";
  readonly measuring: boolean;
  readonly measureKind?: MeasureKind;
  readonly pending: boolean;
  /** How many points of the measurement in hand are down. */
  readonly pendingPoints?: number;
  readonly dimensions: readonly PlanDimension[];
  readonly selectedId: string | null;
  readonly home: DimensionsHome;
  readonly moved?: number;
  readonly stranded?: number;
  readonly error?: string | null;
  readonly onDismissError?: () => void;
  readonly onSelect: (id: string | null) => void;
  readonly onRemove: (id: string) => void;
  readonly onLabel: (id: string, label: string) => void;
  /**
   * May see the dimensions but not add, label or remove one — the matrix's
   * `survey: update`, which an installer on the job does not hold. Each is
   * listed with its label; nothing that would change one is rendered.
   */
  readonly readOnly?: boolean;
}): ReactNode {
  const placed = pendingPoints ?? (pending ? 1 : 0);
  return (
    <aside
      className={`floor-plan__panel floor-plan__panel--${placement}`}
      aria-label="Dimensions you added"
      // Sized by the column it shares with the Objects section
      // (`.floor-plan__side` in FloorPlan.tsx), never by its own content: the
      // plan is fitted to whatever space that column leaves, so the two must
      // never disagree.
      style={{ minWidth: 0 }}
    >
      <p className="floor-plan__panel-title">
        {readOnly ? "Dimensions" : "Your dimensions"} <span className="muted">· {dimensions.length}</span>
      </p>

      {measuring ? (
        <p className="floor-plan__panel-hint" role="status">
          {hintFor(measureKind, placed)}
        </p>
      ) : dimensions.length === 0 && !readOnly ? (
        <p className="floor-plan__panel-hint">
          Press Add dimension to measure between any two points: a room diagonal, a chimney breast, where a
          radiator goes. Angle measures the corner between two legs; Area encloses points and gives the m².
        </p>
      ) : null}

      {error ? (
        <p className="floor-plan__panel-error" role="alert">
          {error}{" "}
          {onDismissError ? (
            <button type="button" className="floor-plan__link" onClick={onDismissError}>
              Dismiss
            </button>
          ) : null}
        </p>
      ) : null}

      {dimensions.length === 0 ? (
        <p className="floor-plan__panel-empty">None on this storey yet.</p>
      ) : (
        <ul className="floor-plan__panel-list">
          {dimensions.map((dimension) => {
            const reading = dimensionReading(dimension);
            const kind = kindOf(dimension);
            const selected = dimension.id === selectedId;
            return (
              <li
                key={dimension.id}
                className={selected ? "floor-plan__panel-row is-selected" : "floor-plan__panel-row"}
                data-measure-kind={kind}
              >
                <button
                  type="button"
                  className="floor-plan__panel-reading"
                  aria-pressed={selected}
                  onClick={() => onSelect(selected ? null : dimension.id)}
                >
                  {reading}
                </button>
                {readOnly ? (
                  dimension.label ? <span className="floor-plan__panel-label-text">{dimension.label}</span> : null
                ) : (
                <>
                <label className="visually-hidden" htmlFor={`plan-dim-${dimension.id}`}>
                  Label for the {reading} {kind === "line" ? "dimension" : KIND_WORD[kind]}
                </label>
                <input
                  id={`plan-dim-${dimension.id}`}
                  className="floor-plan__panel-label"
                  placeholder="Add a label"
                  maxLength={40}
                  // Keyed on the saved label, so a label changed elsewhere
                  // (another tab, a removal that shifts rows) re-seeds the field.
                  key={dimension.label ?? ""}
                  defaultValue={dimension.label ?? ""}
                  onFocus={() => onSelect(dimension.id)}
                  onBlur={(event) => {
                    if (event.target.value.trim() !== (dimension.label ?? "")) {
                      onLabel(dimension.id, event.target.value);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.currentTarget.blur();
                    if (event.key === "Escape") {
                      event.currentTarget.value = dimension.label ?? "";
                      event.currentTarget.blur();
                    }
                  }}
                />
                <button
                  type="button"
                  className="floor-plan__panel-remove"
                  aria-label={`Remove the ${dimension.label ? `${dimension.label} ` : ""}${reading} ${kind === "line" ? "dimension" : KIND_WORD[kind]}`}
                  title="Remove"
                  onClick={() => onRemove(dimension.id)}
                >
                  ×
                </button>
                </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="floor-plan__panel-note" data-testid="plan-storage-note">
        {home === "server" ? (
          <>
            <strong>Saved with the survey.</strong> Everyone on the team sees them, on any device.
            {moved > 0
              ? ` ${moved === 1 ? "One dimension" : `${moved} dimensions`} drawn earlier in this browser ${moved === 1 ? "was" : "were"} moved to the survey.`
              : ""}
            {stranded > 0
              ? ` ${stranded === 1 ? "One dimension is" : `${stranded} dimensions are`} still only in this browser — ${stranded === 1 ? "it" : "they"} could not be moved to the survey yet and will be tried again next time.`
              : ""}
          </>
        ) : home === "loading" ? (
          <>Loading the survey&apos;s dimensions…</>
        ) : home === "memory" ? (
          <>
            <strong>Not saved.</strong> This browser is refusing storage, so these dimensions will be gone when the
            page reloads. Colleagues cannot see them.
          </>
        ) : (
          <>
            Saved <strong>in this browser only</strong>. Colleagues cannot see them, and they will not appear on
            another device or survive clearing this browser&apos;s data.
          </>
        )}
      </p>
    </aside>
  );
}
